// Contracts — datoric shared enums (2026-08-02).
//
// Two vocabularies meet in this project and must not be confused:
//  • OURS — staff roles and the contributor lifecycle we manage.
//  • UPSTREAM — values echoed from the Datoric Provider API. Those are
//    mirrored as loose strings on purpose: their API is account-wide and can
//    add a value at any time, and a strict enum would make our sync throw on
//    data we merely display.
import { z } from 'zod';

// ── ours ────────────────────────────────────────────────────────────────────

// Privilege order (low → high), "highest role wins" scoping — same shape as
// kids. viewer reads its pool, admin also grows it, manager spans all pools.
export const DatoricRoleSchema = z.enum(['viewer', 'admin', 'manager']);
export type DatoricRole = z.infer<typeof DatoricRoleSchema>;

export const DATORIC_ROLE_RANK: Record<DatoricRole, number> = {
  viewer: 0,
  admin: 1,
  manager: 2,
};

/** True if `role` clears a gate requiring `target`. */
export const datoricAllows = (role: DatoricRole | undefined, target: DatoricRole): boolean =>
  role !== undefined && DATORIC_ROLE_RANK[role] >= DATORIC_ROLE_RANK[target];

/** Managers see every contributor; everyone else is confined to their pool. */
export const datoricSeesAllPools = (role: DatoricRole | undefined): boolean => role === 'manager';

// invited — email pushed to the Datoric roster, person has not signed up yet
// joined  — signed up on the platform, no accepted hours yet
// active  — has accepted hours
// blocked — we stopped working with them (upstream anti-fraud is separate)
export const DatoricContributorStatusSchema = z.enum(['invited', 'joined', 'active', 'blocked']);
export type DatoricContributorStatus = z.infer<typeof DatoricContributorStatusSchema>;

// ── upstream (mirrored, intentionally loose) ────────────────────────────────

/** enabled | flagged | disabled — anti-fraud state as reported by Datoric. */
export const DatoricProviderStatusSchema = z.string().nullable();

/** counted | not_matched | pending | accepted | rejected | mixed. */
export const DatoricUpstreamStatusSchema = z.string().nullable();

/** pending | approved | rejected. */
export const DatoricReviewStatusSchema = z.string().nullable();

/** pending | processing | completed | failed — the scoring pipeline. */
export const DatoricPipelineStatusSchema = z.string().nullable();

// ── shared atoms ────────────────────────────────────────────────────────────

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Contributor email — THE join key to Datoric. Lowercased by the worker. */
export const DatoricEmailSchema = z.string().trim().regex(EMAIL_RE).max(200);

/** Epoch ms, nullable: upstream timestamps are frequently absent. */
export const DatoricTsSchema = z.number().int().nullable();

/**
 * Money in cents. Never a float — payout arithmetic on fractional dollars
 * accumulates error across thousands of recordings.
 */
export const DatoricCentsSchema = z.number().int();
